import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  AnnouncementPanel,
  Button,
  HeadingSection,
  Typography,
} from '@innovamat/glimmer-components';
import type { Organization } from '@innovamat/glow-api-client';
import { useClassroomQuery } from '@innovamat/glow-api-client';
import { useClassroomTeachers } from '../../hooks/use-classroom-teachers';

import {
  UpdatePeriodAnnouncementPanel,
  useCheckIsUpdatePeriod,
} from '../../../components/update-period-announcement-panel';
import { useCurrentNavigationValues } from '../../../contents';
import { APP_PATHS, useNavigation } from '../../../navigation';
import { useUser } from '../../../user-management';
import { useClassroomCapabilities } from '../../hooks/use-classroom-capabilities';
import { useOrganization } from '../../hooks/use-organization';
import type { Classroom } from '../../modules/classroom';
import { AssignTeachersToClassroomModal } from './components/assign-teachers-to-classroom-modal';
import { ClassroomTeachersTable } from './components/classroom-teachers-table';
import { ClassroomTeachersTableSkeleton } from './components/classroom-teachers-table/classroom-teachers-table.skeleton';

const ChaeckClassroomAfterUpdatePeriodAnnouncementPanel = styled(
  AnnouncementPanel
)`
  margin-top: 1rem;
`;

function ClassroomTeachers(): JSX.Element | null {
  const { classroomId } = useParams<{ classroomId: string }>();
  const [
    isAddTeacherToClassroomModalOpen,
    setIsAddTeacherToClassroomModalOpen,
  ] = useState(false);
  const { t } = useTranslation();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { currentClassroom } = useCurrentNavigationValues();
  const { navigateInSameNavType } = useNavigation();
  const isUpdatePeriod = useCheckIsUpdatePeriod();

  const { isPlatformEnabled, organization } = useOrganization();

  const organizationId = user?.organizationId ?? '';

  const { data: classroomQuery, isLoading: isLoadingClassroom } =
    useClassroomQuery(
      {
        id: classroomId ?? '',
      },
      {
        enabled: !!classroomId && !!organization?.id,
      }
    );

  const {
    data: teachers,
    isLoading: isLoadingTeachers,
    refetch: refetchClassroomTeachers,
  } = useClassroomTeachers({
    classroomId: classroomId ?? '',
    organizationId,
    isPlatformEnabled,
  });

  const classroomName = classroomQuery?.classroom?.name ?? '';
  const currentClassroomData = classroomQuery?.classroom;

  const {
    classroom: { canClassroomTeacherLeave },
  } = useClassroomCapabilities({
    classroom: currentClassroomData as Classroom,
  });

  const isLoadingManagementData = isLoadingClassroom || !organization;

  const shouldAllowAddingTeachersToClassroom =
    !isUpdatePeriod && !isLoadingManagementData;

  const onSuccessAddTeachers = (): void => {
    refetchClassroomTeachers();

    queryClient.invalidateQueries({
      queryKey: ['Classrooms'],
    });
  };

  const isClassroomCheckedAfterUpdatePeriod =
    !currentClassroom?.checked ||
    (currentClassroom.provider && !currentClassroom.providerChecked);

  return (
    <>
      <HeadingSection
        title={t('reports.admin.title.teachers')}
        actions={
          shouldAllowAddingTeachersToClassroom && (
            <Button
              disabled={isUpdatePeriod}
              onClick={() => setIsAddTeacherToClassroomModalOpen(true)}
              rightIcon="AddUpIcon"
              variant="secondary"
            >
              {t('teachers.buttons.addTeachers')}
            </Button>
          )
        }
      />
      {isClassroomCheckedAfterUpdatePeriod && (
        <ChaeckClassroomAfterUpdatePeriodAnnouncementPanel
          text={
            <Trans
              i18nKey="classroom.updatePeriod.announcement"
              components={{
                a: (
                  <Typography.Link2
                    onClick={() =>
                      navigateInSameNavType(APP_PATHS.CLASSROOM_STUDENTS_URL)
                    }
                  />
                ),
              }}
            />
          }
          type="imported"
          canClose={false}
        />
      )}

      <UpdatePeriodAnnouncementPanel />

      {isLoadingTeachers || isLoadingManagementData || !classroomId ? (
        <ClassroomTeachersTableSkeleton />
      ) : (
        <ClassroomTeachersTable
          classroomId={classroomId!}
          classroomName={classroomName}
          isUpdatePeriod={isUpdatePeriod}
          shouldAllowAddingTeachersToClassroom={
            shouldAllowAddingTeachersToClassroom
          }
          isPlatformEnabled={isPlatformEnabled}
          onAddTeachers={() => setIsAddTeacherToClassroomModalOpen(true)}
          organizationId={organizationId}
          teachers={teachers}
          canLeave={canClassroomTeacherLeave}
        />
      )}

      {isAddTeacherToClassroomModalOpen && (
        <AssignTeachersToClassroomModal
          classroomId={classroomId!}
          classroomName={classroomName}
          onClose={() => setIsAddTeacherToClassroomModalOpen(false)}
          onSuccess={onSuccessAddTeachers}
          organization={organization as Organization}
        />
      )}
    </>
  );
}

export { ClassroomTeachers };
